import type { Plan, PlanLimitKey, PlanLimits } from "@esi/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppDialog } from "@/components/AppDialog";
import { NativeSelect } from "@/components/NativeSelect";
import { Notice } from "@/components/Notice";
import { Page } from "@/components/Page";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "../api.ts";
import { apiError, ok } from "../api-errors.ts";
import { useCsrfHeaders } from "./operator.tsx";
import { consoleRefusal, limitText } from "./parts.tsx";
import { consoleKeys, usePlans } from "./queries.ts";
import { LIMIT_FIELDS } from "./TenantDetail.tsx";

/** Plans (spec `tenant-plans`): limits only, no prices; one is the instance's default. */
export function PlansPage() {
  const { t } = useTranslation();
  const plans = usePlans();
  const [editing, setEditing] = useState<Plan | "new" | null>(null);
  return (
    <Page width="wide">
      <PageHeader
        title={t("console.plans.title")}
        lead={t("console.plans.lead")}
        actions={
          <Button onClick={() => setEditing("new")}>
            <Plus aria-hidden="true" />
            {t("console.plans.create")}
          </Button>
        }
      />
      {plans.isError ? (
        <Notice tone="danger">{plans.error.message}</Notice>
      ) : !plans.data ? (
        <Skeleton className="h-40 w-full" />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-line bg-surface-raised">
          <table className="w-full min-w-[52rem] text-left text-sm">
            <caption className="sr-only">{t("console.plans.title")}</caption>
            <thead className="border-b border-line text-xs text-fg-muted">
              <tr>
                <th scope="col" className="px-4 py-2 font-medium">
                  {t("console.plans.name")}
                </th>
                {LIMIT_FIELDS.map((f) => (
                  <th key={f.key} scope="col" className="px-4 py-2 text-right font-medium">
                    {t(`console.limits.${f.key}`)}
                  </th>
                ))}
                <th scope="col" className="px-4 py-2 font-medium">
                  {t("console.plans.mode")}
                </th>
                <th scope="col" className="px-4 py-2 text-right font-medium">
                  {t("console.plans.tenants")}
                </th>
                <th scope="col" className="px-4 py-2">
                  <span className="sr-only">{t("users.actions")}</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {plans.data.map((plan) => (
                <tr key={plan.id}>
                  <th scope="row" className="px-4 py-2.5 text-left font-medium text-fg">
                    {plan.name}
                    {plan.isDefault ? (
                      <span className="ml-2 rounded-sm bg-surface-sunken px-1.5 py-0.5 text-xs font-medium text-fg-secondary">
                        {t("console.plans.default")}
                      </span>
                    ) : null}
                  </th>
                  {LIMIT_FIELDS.map((f) => (
                    <td
                      key={f.key}
                      className="px-4 py-2.5 text-right text-fg-secondary tabular-nums"
                    >
                      {limitText(plan.limits[f.key], f.format)}
                    </td>
                  ))}
                  <td className="px-4 py-2.5 text-fg-secondary">
                    {t(`console.plans.modes.${plan.modelLimitMode}`)}
                  </td>
                  <td className="px-4 py-2.5 text-right text-fg-secondary tabular-nums">
                    {plan.tenantCount}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={t("console.plans.editOf", { name: plan.name })}
                      onClick={() => setEditing(plan)}
                    >
                      {t("console.plans.edit")}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing ? (
        <PlanDialog plan={editing === "new" ? null : editing} onClose={() => setEditing(null)} />
      ) : null}
    </Page>
  );
}

function PlanDialog(props: { plan: Plan | null; onClose: () => void }) {
  const { t } = useTranslation();
  const id = useId();
  const headers = useCsrfHeaders();
  const queryClient = useQueryClient();
  const [name, setName] = useState(props.plan?.name ?? "");
  const [inputs, setInputs] = useState<Record<PlanLimitKey, string>>(
    () =>
      Object.fromEntries(
        LIMIT_FIELDS.map((f) => {
          const v = props.plan?.limits[f.key] ?? null;
          return [f.key, v === null ? "" : f.toInput(v)];
        }),
      ) as Record<PlanLimitKey, string>,
  );
  const [mode, setMode] = useState<"soft" | "hard">(props.plan?.modelLimitMode ?? "soft");
  const [isDefault, setIsDefault] = useState(props.plan?.isDefault ?? false);
  const limits = Object.fromEntries(
    LIMIT_FIELDS.map((f) => [
      f.key,
      inputs[f.key].trim() === "" ? null : f.fromInput(inputs[f.key]),
    ]),
  ) as PlanLimits;
  const settle = () => queryClient.invalidateQueries({ queryKey: consoleKeys.plans });
  const save = useMutation({
    mutationFn: async () => {
      const json = { name: name.trim(), limits, modelLimitMode: mode, isDefault };
      return props.plan
        ? ok(
            await api.api.operator.plans[":planId"].$put(
              { param: { planId: props.plan.id }, json },
              headers,
            ),
          )
        : ok(await api.api.operator.plans.$post({ json }, headers));
    },
    onSuccess: async () => {
      await settle();
      props.onClose();
    },
  });
  const remove = useMutation({
    mutationFn: async () => {
      const res = await api.api.operator.plans[":planId"].$delete(
        { param: { planId: props.plan?.id ?? "" } },
        headers,
      );
      if (!res.ok) {
        throw await apiError(res);
      }
    },
    onSuccess: async () => {
      await settle();
      props.onClose();
    },
  });
  const error = save.error ?? remove.error;
  return (
    <AppDialog
      open
      title={
        props.plan
          ? t("console.plans.editTitle", { name: props.plan.name })
          : t("console.plans.createTitle")
      }
      description={t("console.plans.dialogBody")}
      onClose={props.onClose}
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim() && !save.isPending) {
            save.mutate();
          }
        }}
      >
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${id}-name`}>{t("console.plans.name")}</Label>
          <Input
            id={`${id}-name`}
            required
            maxLength={80}
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          {LIMIT_FIELDS.map((f) => (
            <div key={f.key} className="flex flex-col gap-1.5">
              <Label htmlFor={`${id}-${f.key}`}>{t(`console.limits.${f.key}`)}</Label>
              <Input
                id={`${id}-${f.key}`}
                type="number"
                min={0}
                step={f.step}
                inputMode="decimal"
                placeholder={t("plan.unlimited")}
                value={inputs[f.key]}
                onChange={(e) => setInputs({ ...inputs, [f.key]: e.currentTarget.value })}
              />
            </div>
          ))}
        </div>
        <p className="text-xs text-fg-muted">{t("console.plans.emptyIsUnlimited")}</p>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${id}-mode`}>{t("console.plans.mode")}</Label>
          <NativeSelect
            id={`${id}-mode`}
            value={mode}
            onChange={(e) => setMode(e.currentTarget.value as "soft" | "hard")}
          >
            <option value="soft">{t("console.plans.modes.soft")}</option>
            <option value="hard">{t("console.plans.modes.hard")}</option>
          </NativeSelect>
        </div>
        {props.plan?.isDefault ? (
          <p className="text-sm text-fg-secondary">{t("console.plans.isDefault")}</p>
        ) : (
          <div className="flex items-center gap-2">
            <Checkbox
              id={`${id}-default`}
              checked={isDefault}
              onCheckedChange={(on) => setIsDefault(on === true)}
            />
            <Label htmlFor={`${id}-default`} className="font-normal">
              {t("console.plans.makeDefault")}
            </Label>
          </div>
        )}
        {error ? <Notice tone="danger">{consoleRefusal(error)}</Notice> : null}
        <div className="flex flex-wrap items-center justify-between gap-2">
          {props.plan && !props.plan.isDefault ? (
            <Button
              type="button"
              variant="ghost"
              className="text-danger-fg"
              disabled={remove.isPending}
              onClick={() => {
                save.reset();
                remove.mutate();
              }}
            >
              {t("console.plans.delete")}
            </Button>
          ) : (
            <span />
          )}
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="ghost" onClick={props.onClose}>
              {t("app.cancel")}
            </Button>
            <Button type="submit" disabled={!name.trim() || save.isPending}>
              {t("app.save")}
            </Button>
          </div>
        </div>
      </form>
    </AppDialog>
  );
}
